import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Terminal, ArrowRight, Menu, X } from "lucide-react";

export default function PublicLayout({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const isActive = (path) => location.pathname === path;

    const navLinks = [
        { label: "Features", path: "/features" },
        { label: "How it works", path: "/how-it-works" },
        { label: "Pricing", path: "/pricing" },
        { label: "Docs", path: "/docs" }
    ];

    return (
        <div className="min-h-screen bg-[#050505] text-[#FAFAFA] flex flex-col font-mono selection:bg-[#00FF41] selection:text-black">
            {/* Header Navigation */}
            <header className="border-b border-[#27272A] sticky top-0 z-40 bg-[#050505]/90 backdrop-blur">
                <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-4">
                    <Link to="/" className="flex items-center gap-2 group" data-testid="public-logo">
                        <Terminal className="w-5 h-5 text-[#00FF41] transition-transform duration-300 group-hover:rotate-12" strokeWidth={2.5} />
                        <span className="font-display text-xl uppercase tracking-tight font-bold transition-colors duration-300 group-hover:text-[#00FF41]">SYNAPSELINK</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-widest text-[#A1A1AA]">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`transition-colors duration-200 hover:text-[#00FF41] ${isActive(link.path) ? "text-[#00FF41] font-bold" : ""}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                        {user && user.role === "admin" && (
                            <Link to="/app/admin" className="text-[#FF9900] hover:underline font-bold">ADMIN</Link>
                        )}
                    </nav>

                    {/* Desktop Auth Buttons */}
                    <div className="hidden md:flex items-center gap-3">
                        {user ? (
                            <Link to="/app/tunnels" className="tf-btn" data-testid="nav-dashboard">
                                DASHBOARD <ArrowRight className="w-4 h-4" />
                            </Link>
                        ) : (
                            <>
                                <Link to="/login" className="text-xs uppercase tracking-widest text-[#A1A1AA] hover:text-[#FAFAFA]" data-testid="nav-login">Login</Link>
                                <Link to="/register" className="tf-btn" data-testid="nav-register">
                                    GET URL <ArrowRight className="w-4 h-4" />
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button 
                        className="md:hidden text-[#A1A1AA] hover:text-[#FAFAFA]" 
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle Menu"
                    >
                        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile Dropdown Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-[#27272A] bg-[#0A0A0A] px-6 py-6 space-y-4 flex flex-col font-mono">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={`text-sm uppercase tracking-wider block transition-colors duration-200 ${isActive(link.path) ? "text-[#00FF41]" : "text-[#A1A1AA]"}`}
                            >
                                {link.label}
                            </Link>
                        ))}
                        {user && user.role === "admin" && (
                            <Link 
                                to="/app/admin" 
                                onClick={() => setMobileMenuOpen(false)}
                                className="text-[#FF9900] text-sm uppercase tracking-wider block font-bold"
                            >
                                ADMIN
                            </Link>
                        )}
                        <hr className="border-[#27272A]" />
                        {user ? (
                            <Link 
                                to="/app/tunnels" 
                                onClick={() => setMobileMenuOpen(false)}
                                className="tf-btn w-full justify-center"
                            >
                                DASHBOARD <ArrowRight className="w-4 h-4" />
                            </Link>
                        ) : (
                            <div className="flex flex-col gap-4">
                                <Link 
                                    to="/login" 
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="text-sm uppercase tracking-wider text-[#A1A1AA] text-center py-2 hover:text-[#FAFAFA]"
                                >
                                    Login
                                </Link>
                                <Link 
                                    to="/register" 
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="tf-btn w-full justify-center"
                                >
                                    GET URL <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        )}
                    </div>
                )}
            </header>

            {/* Main content */}
            <main className="flex-grow">
                {children}
            </main>

            {/* Public Footer */}
            <footer className="bg-[#050505] border-t border-[#27272A] mt-auto">
                <div className="max-w-[1400px] mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs uppercase tracking-widest text-[#52525B]">
                    <div className="flex items-center gap-2">
                        <Terminal className="w-4 h-4 text-[#00FF41]" /> SYNAPSELINK © 2026
                    </div>
                    <div className="flex items-center gap-6">
                        <Link to="/ngrok-alternative" className="hover:text-[#FAFAFA] transition-colors duration-200">Ngrok Alternative</Link>
                        <Link to="/status" className="hover:text-[#FAFAFA] transition-colors duration-200">Status</Link>
                        <Link to="/docs" className="hover:text-[#FAFAFA] transition-colors duration-200">Docs</Link>
                        <Link to="/privacy" className="hover:text-[#FAFAFA] transition-colors duration-200">Privacy</Link>
                        <Link to="/terms" className="hover:text-[#FAFAFA] transition-colors duration-200">Terms</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
